import { useGetMediaQuery } from "../redux/services/media/mediaApi";
import {
  buildLoadRequirementAlerts,
  evidenceModeFromConfig,
  legHasRequirementException,
} from "../utils/loadRequirementAlerts";

/**
 * Persistent warning banners. They stay until the missing item is on the load.
 * @param {{ alerts?: { id: string, title: string, message: string }[] }} props
 */
export function RequirementAlertBanners({ alerts = [] }) {
  if (!alerts.length) return null;

  return (
    <div className="space-y-2">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          role="alert"
          className="rounded border border-amber-300 bg-amber-50 px-4 py-3 text-amber-950"
        >
          <div className="text-sm font-semibold">{alert.title}</div>
          <p className="mt-1 text-sm">{alert.message}</p>
        </div>
      ))}
    </div>
  );
}

/**
 * Alerts for one stop. Loads that stop's files so a later upload clears the warning.
 * @param {{ leg: object, configs?: object, showStop?: boolean }} props
 */
export function LegRequirementAlerts({ leg, configs, showStop = false }) {
  const needsCheck = legHasRequirementException(leg);
  const { data: media, isSuccess } = useGetMediaQuery(
    { refId: leg?._id, limit: 50 },
    { skip: !leg?._id || !needsCheck },
  );

  if (!needsCheck) return null;

  const alerts = buildLoadRequirementAlerts({
    leg,
    media,
    evidenceMode: evidenceModeFromConfig(configs),
    mediaReady: isSuccess,
  }).map((alert) => {
    const stop = leg?.orderNumber || leg?.loadNumber;
    if (!showStop || !stop) return alert;
    return {
      ...alert,
      id: `${leg._id}-${alert.id}`,
      title: `${stop}: ${alert.title}`,
    };
  });

  return <RequirementAlertBanners alerts={alerts} />;
}

/**
 * Alerts for every stop on a job.
 * @param {{ legs?: object[], configs?: object }} props
 */
export function JobRequirementAlerts({ legs = [], configs }) {
  const flagged = (legs || []).filter(
    (leg) => leg?._id && !leg.isDraft && legHasRequirementException(leg),
  );
  if (!flagged.length) return null;

  return (
    <div className="space-y-2 px-4">
      {flagged.map((leg) => (
        <LegRequirementAlerts
          key={leg._id}
          leg={leg}
          configs={configs}
          showStop={flagged.length > 1}
        />
      ))}
    </div>
  );
}
